import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { todayLocal, type LastImportDto } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { api } from '../lib/api.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Les deux vues de Mnemo. Un onglet est un **segment de chemin** (Charte §6), et les
 * filtres restent dans la query-string : passer de l'une à l'autre garde le contexte,
 * et les deux URL se partagent telles quelles.
 *
 * L'administration n'est plus un onglet : elle vit sous la roue dentée, avec les réglages
 * (24.09.2026). Quand on y est, un onglet inerte le rappelle — sans lui, la barre ne
 * montrerait aucun onglet actif, et on ne saurait plus où l'on est.
 */
export function Tabs({ t, locale, userId }: { t: T; locale: Locale; userId?: string }) {
  const { search, pathname } = useLocation();
  const onglets: { to: string; key: TranslationKey }[] = [
    { to: '/', key: 'nav.jobs' },
    { to: '/synthese', key: 'nav.summary' },
    // Le décompte par personne (DEC-01) : ce qu'on refacture, personne par personne.
    { to: '/decompte', key: 'nav.decompte' },
  ];
  const inAdmin = pathname.startsWith('/admin');

  return (
    <nav className="border-b border-border bg-surface" aria-label={t('nav.label')}>
      <div className="mx-auto flex max-w-7xl items-center gap-1 px-4">
        <ul className="flex gap-1">
          {onglets.map((onglet) => (
            <li key={onglet.to}>
              <NavLink
                to={{ pathname: onglet.to, search: inAdmin ? '' : search }}
                end={onglet.to === '/'}
                className={({ isActive }) =>
                  `inline-flex min-h-[44px] items-center border-b-2 px-3 text-sm font-medium transition-colors ${
                    isActive
                      ? 'border-accent text-accent'
                      : 'border-transparent text-muted hover:text-fg'
                  }`
                }
              >
                {t(onglet.key)}
              </NavLink>
            </li>
          ))}
          {inAdmin && (
            <li>
              <span
                aria-current="page"
                className="inline-flex min-h-[44px] items-center border-b-2 border-accent px-3 text-sm font-medium text-accent"
              >
                {t('nav.admin')}
              </span>
            </li>
          )}
        </ul>
        <LastImport t={t} locale={locale} userId={userId} />
      </div>
    </nav>
  );
}

/**
 * « Dernière importation : aujourd'hui à 02:04 » — d'un coup d'œil, sans question à poser
 * (VUE-20, demandé le 25.09.2026). Le point passe à l'orange au-delà du seuil de la
 * surveillance : les impressions récentes peuvent alors manquer.
 *
 * Secondaire : un échec de chargement ne doit ni bloquer ni alerter (Charte §5) — la
 * mention disparaît, simplement.
 */
function LastImport({ t, locale, userId }: { t: T; locale: Locale; userId?: string }) {
  const [data, setData] = useState<LastImportDto | null>(null);

  useEffect(() => {
    api
      .lastImport()
      .then(setData)
      .catch(() => setData(null));
  }, [userId]);

  const label = useMemo(() => {
    if (!data) return null;
    if (!data.lastImportAt) return t('lastImport.none');
    const at = new Date(data.lastImportAt);
    const tag = locale === 'de' ? 'de-CH' : 'fr-CH';
    const time = new Intl.DateTimeFormat(tag, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Zurich',
    }).format(at);
    // Le jour se juge à l'heure suisse : 00 h 30 à Fribourg, c'est déjà « aujourd'hui ».
    const day = todayLocal(at);
    const now = new Date();
    if (day === todayLocal(now)) return t('lastImport.today', { time });
    if (day === todayLocal(new Date(now.getTime() - 86_400_000)))
      return t('lastImport.yesterday', { time });
    const date = new Intl.DateTimeFormat(tag, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'Europe/Zurich',
    }).format(at);
    return t('lastImport.on', { date, time });
  }, [data, locale, t]);

  if (!data || !label) return null;

  const hint = data.stale
    ? t('lastImport.stale', { hours: data.thresholdHours })
    : t('lastImport.hint');

  return (
    <p className="ml-auto flex items-center gap-1.5 py-2 text-xs text-muted" title={hint}>
      {/* Jamais la couleur seule (Charte §7) : la bulle dit la même chose en toutes lettres. */}
      <span
        aria-hidden="true"
        className={`h-2 w-2 shrink-0 rounded-full ${data.stale ? 'bg-warn' : 'bg-accent'}`}
      />
      <span className="hidden sm:inline">{t('lastImport.label')}</span>
      <time dateTime={data.lastImportAt ?? undefined} className="font-medium text-fg">
        {label}
      </time>
      <span className="sr-only">{hint}</span>
    </p>
  );
}
